import React, { useMemo } from 'react';
import { Table, Tag, Space, Typography, Avatar, Tooltip, Skeleton, Flex, Grid } from 'antd';
import {
    UserOutlined,
    RobotOutlined,
    CommentOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const STATUS_COLORS = {
    open: 'blue',
    in_progress: 'orange',
    resolved: 'green',
    closed: 'default',
};

const STATUS_DOT_COLORS = {
    open: '#1677ff',
    in_progress: '#fa8c16',
    resolved: '#52c41a',
    closed: '#8c8c8c',
};

const PRIORITY_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

const CATEGORY_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};

/**
 * Returns a human-friendly relative time string.
 */
const timeAgo = (dateString) => {
    if (!dateString) return '';
    try {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 30) return `${diffDays}d`;
        return `${Math.floor(diffDays / 30)}mo`;
    } catch {
        return '';
    }
};

/**
 * Short ticket ID (#A3B8D)
 */
const shortId = (id) => {
    if (!id) return '';
    return `#${id.slice(0, 5).toUpperCase()}`;
};

/* ─── Mobile Card ─── */
const TicketCard = ({ ticket, isDarkMode, t, onClick }) => (
    <div
        className={`ticket-list-card ${isDarkMode ? 'card-dark' : 'card-light'}`}
        onClick={onClick}
    >
        {/* Priority bar */}
        {ticket.priority && (
            <div
                className="card-priority-indicator"
                style={{
                    backgroundColor:
                        ticket.priority === 'urgent' ? '#ff4d4f' :
                            ticket.priority === 'high' ? '#fa8c16' :
                                ticket.priority === 'medium' ? '#1677ff' : '#8c8c8c',
                }}
            />
        )}

        {/* Top row: ID + Time */}
        <Flex align="center" justify="space-between" style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, opacity: 0.5 }}>
                {shortId(ticket.id)}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
                {timeAgo(ticket.created_at)}
            </Text>
        </Flex>

        {/* Subject */}
        <Text
            strong
            style={{
                fontSize: 13,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: 8,
            }}
        >
            {ticket.subject || t('no_subject')}
        </Text>

        {/* Tags row */}
        <Flex wrap="wrap" gap={4} style={{ marginBottom: 8 }}>
            <Tag color={STATUS_COLORS[ticket.status]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                <span style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                    backgroundColor: STATUS_DOT_COLORS[ticket.status], marginInlineEnd: 4, verticalAlign: 'middle',
                }} />
                {t(`ticket_status_${ticket.status}`)}
            </Tag>
            {ticket.priority && (
                <Tag color={PRIORITY_COLORS[ticket.priority]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                    {t(`ticket_priority_${ticket.priority}`)}
                </Tag>
            )}
            {ticket.category && (
                <Tag color={CATEGORY_COLORS[ticket.category]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                    {t(`ticket_category_${ticket.category}`)}
                </Tag>
            )}
        </Flex>

        {/* Footer: Contact + Comments */}
        <Flex align="center" justify="space-between" style={{ opacity: 0.6, fontSize: 11 }}>
            <Space size={6}>
                {ticket.contact ? (
                    <>
                        <Avatar
                            size={18}
                            src={ticket.contact.picture_url}
                            icon={<UserOutlined />}
                            style={{
                                background: isDarkMode ? '#264f78' : '#e6f4ff',
                                color: isDarkMode ? '#69b1ff' : '#1677ff',
                                fontSize: 9,
                            }}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>{ticket.contact.full_name}</Text>
                    </>
                ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {ticket.opened_by_type === 'agent' ? <RobotOutlined /> : <UserOutlined />}
                    </Text>
                )}
            </Space>
            {ticket.comment_count > 0 && (
                <Space size={3}>
                    <CommentOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary" style={{ fontSize: 11 }}>{ticket.comment_count}</Text>
                </Space>
            )}
        </Flex>
    </div>
);

/* ─── Main Component ─── */
const TicketTableView = ({ tickets, onStatusChange, skeleton }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint();
    const isCompact = !screens.md;

    const columns = useMemo(() => [
        {
            title: t('ticket_subject'),
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
            render: (text, record) => (
                <div style={{ minWidth: 0 }}>
                    <Text
                        strong
                        style={{
                            fontSize: 13,
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}
                    >
                        {text || t('no_subject')}
                    </Text>
                    {record.description && record.description !== text && (
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 11,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                marginTop: 1,
                            }}
                        >
                            {record.description}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: t('ticket_status'),
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (status) => (
                <Tag color={STATUS_COLORS[status]} style={{ margin: 0, borderRadius: 4, fontWeight: 500 }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: STATUS_DOT_COLORS[status],
                            marginInlineEnd: 5,
                            verticalAlign: 'middle',
                        }}
                    />
                    {t(`ticket_status_${status}`)}
                </Tag>
            ),
            filters: ['open', 'in_progress', 'resolved', 'closed'].map(s => ({
                text: t(`ticket_status_${s}`),
                value: s,
            })),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: t('ticket_priority'),
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority) => priority ? (
                <Tag color={PRIORITY_COLORS[priority]} style={{ margin: 0, borderRadius: 4 }}>
                    {t(`ticket_priority_${priority}`)}
                </Tag>
            ) : null,
        },
        {
            title: t('ticket_category'),
            dataIndex: 'category',
            key: 'category',
            width: 120,
            responsive: ['md'],
            render: (category) => category ? (
                <Tag color={CATEGORY_COLORS[category]} style={{ margin: 0, borderRadius: 4 }}>
                    {t(`ticket_category_${category}`)}
                </Tag>
            ) : null,
        },
        {
            title: t('contact'),
            dataIndex: 'contact',
            key: 'contact',
            width: 150,
            responsive: ['lg'],
            render: (contact) => contact ? (
                <Space size={6}>
                    <Avatar
                        size={22}
                        src={contact.picture_url}
                        icon={<UserOutlined />}
                        style={{
                            background: isDarkMode ? '#264f78' : '#e6f4ff',
                            color: isDarkMode ? '#69b1ff' : '#1677ff',
                            fontSize: 11,
                        }}
                    />
                    <Text style={{ fontSize: 12 }}>{contact.full_name}</Text>
                </Space>
            ) : null,
        },
        {
            title: <CommentOutlined />,
            dataIndex: 'comment_count',
            key: 'comments',
            width: 55,
            align: 'center',
            responsive: ['md'],
            render: (count) => count > 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>{count}</Text>
            ) : null,
        },
        {
            title: <ClockCircleOutlined />,
            dataIndex: 'created_at',
            key: 'created_at',
            width: 70,
            align: 'center',
            render: (date) => (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {timeAgo(date)}
                </Text>
            ),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: 'descend',
        },
    ], [t, isDarkMode]);

    if (skeleton) {
        return (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} active paragraph={{ rows: isCompact ? 2 : 1 }} />
                ))}
            </div>
        );
    }

    /* ─── Mobile: Card List ─── */
    if (isCompact) {
        return (
            <div className="ticket-list-mobile">
                {tickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                        <Text type="secondary">{t('no_tickets')}</Text>
                    </div>
                ) : (
                    tickets.map((ticket) => (
                        <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            isDarkMode={isDarkMode}
                            t={t}
                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                        />
                    ))
                )}
            </div>
        );
    }

    /* ─── Desktop: Table ─── */
    return (
        <Table
            dataSource={tickets}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ y: 'calc(100vh - 280px)' }}
            style={{ borderRadius: 8, overflow: 'hidden' }}
            onRow={(record) => ({
                onClick: () => navigate(`/tickets/${record.id}`),
                style: { cursor: 'pointer' },
            })}
            locale={{
                emptyText: t('no_tickets'),
            }}
        />
    );
};

export default TicketTableView;
